/**
 * Explore Unknown — battle screen (BattleUI) backgrounds by global mission index (React client copy).
 *
 * Canonical implementation lives at `modules/exploreUnknownMissionBattle.js` on the
 * server. This file is a byte-identical duplicate because Create React App
 * cannot import from outside `React/src/`. Keep both files in sync;
 * `__tests__/regression/ExploreUnknown_MissionBattle_Parity_Regression.js`
 * enforces functional parity on CI.
 *
 * Backgrounds live under `public/unknown_backgrounds/` and are excluded from faction random rotation.
 * Add a row per mission that should use fixed art; unlisted indices use normal faction-based rotation.
 */

const EXPLORE_UNKNOWN_MISSION_BATTLE_BACKGROUNDS = {
  1: 'woods_a.jpg',
  2: 'lake_a.jpg',
  3: 'dinner_woods.jpg',
  4: 'pond.jpg',
  5: 'arc.jpg',
  6: 'cave1.jpg',
  7: 'cave2.jpg',
  8: 'cave3.jpg',
  9: 'cave4.jpg',
  10: 'images/boss10.jpg',
  11: 'desert1.jpg',
  12: 'desert2.jpg',
  13: 'desert3.jpg',
  14: 'desert4.jpg',
  15: 'desert5.jpg',
  16: 'images/boss16.jpg',
  17: 'fair1.jpg',
  18: 'fair2.jpg',
  19: 'fair3.jpg',
  20: 'fair4.jpg',
  21: 'fair5.jpg',
  22: 'fair6.jpg',
  23: 'images/boss23.jpg',
  24: 'lavender1.jpg',
  25: 'lavender2.jpg',
  26: 'lavender3.jpg',
  27: 'images/boss27.jpg',
  28: 'lavender4.jpg',
  29: 'treetop_bg.jpg',
  30: 'open_sea_1.jpg',
  31: 'open_sea_2.jpg',
  32: 'open_sea_3.jpg',
  33: 'images/boss33.jpg',
  34: 'open_sea_4.jpg',
  35: 'pixelpast1.jpg',
  36: 'pixelpast2.jpg',
  37: 'pixelpast3.jpg',
  38: 'pixelpast4.jpg',
  39: 'pixelpast5.jpg',
  40: 'pixelpast6.jpg',
  41: 'kendrex1.jpg',
  42: 'kendrex2.jpg',
  43: 'kendrex3.jpg',
  44: 'kendrex4.jpg',
  45: 'kendrex5.jpg',
  46: 'images/boss46.jpg',
  47: 'autobahn1.jpg',
  48: 'autobahn2.jpg',
  49: 'autobahn3.jpg',
  50: 'autobahn4.jpg',
  51: 'autobahn5.jpg',
  52: 'autobahn6.jpg',
  53: 'images/boss53.jpg',
  54: 'production1.jpg',
  55: 'production2.jpg',
  56: 'production3.jpg',
  57: 'production4.jpg',
  58: 'production5.jpg',
  59: 'images/boss59.jpg',
  60: 'production6.jpg',
  61: 'awakening1.jpg',
  62: 'awakening2.jpg',
  63: 'awakening3.jpg',
  64: 'awakening4.jpg',
  65: 'awakening5.jpg',
  66: 'awakening6.jpg',
  67: 'awakening7.jpg',
  68: 'images/boss68.jpg',
  69: 'core1.jpg',
  70: 'core2.jpg',
  71: 'core3.jpg',
  72: 'core4.jpg',
  73: 'images/boss73.jpg',
  74: 'images/boss74.jpg',
  75: 'images/boss75.jpg',

};

/** First global mission index on each map surface (skip-world choice preview art). */
const EXPLORE_UNKNOWN_SURFACE_FIRST_MISSION_INDEX = Object.freeze({
  arrival: 1,
  cave: 6,
  desert: 11,
  fair: 17,
  lavender: 24,
  treetop: 29,
  boat: 30,
  pixelpast: 35,
  kendrex: 41,
  autobahn: 47,
  productionhell: 54,
  awakening: 61,
  core: 69,
});

/**
 * @param {string} surface — `arrival` | `cave` | `desert` | `fair` | `lavender` | `treetop` | `boat` | `pixelpast` | `kendrex`
 * @returns {string} URL path for the first mission battle background on that surface (e.g. `/unknown_backgrounds/fair1.jpg`)
 */
function getExploreUnknownSurfaceFirstMissionPreviewUrl(surface) {
  const idx = EXPLORE_UNKNOWN_SURFACE_FIRST_MISSION_INDEX[surface];
  if (idx == null) {
    throw new Error(`Explore Unknown: no first mission index for surface (${String(surface)})`);
  }
  const backgroundPath = tryGetExploreUnknownBattleBackgroundPath(idx);
  if (!backgroundPath) {
    throw new Error(
      `Explore Unknown: no battle background for surface ${String(surface)} mission ${idx}`
    );
  }
  if (/\.(mp4|webm)$/i.test(backgroundPath)) {
    throw new Error(
      `Explore Unknown: first mission preview must be an image, not video (${backgroundPath})`
    );
  }
  return resolveBattleBackgroundCssUrl(backgroundPath);
}

/**
 * @param {unknown} missionIndex — `gameState.exploreUnknownMissionIndex`
 * @returns {string | null} e.g. `unknown_backgrounds/woods_a.jpg`, or null when this mission has no fixed art (use faction rotation)
 */
function tryGetExploreUnknownBattleBackgroundPath(missionIndex) {
  const idx = Number(missionIndex);
  if (!Number.isInteger(idx) || idx < 1) {
    return null;
  }
  const file = EXPLORE_UNKNOWN_MISSION_BATTLE_BACKGROUNDS[idx];
  if (!file) {
    return null;
  }
  if (file.startsWith('images/')) {
    return file;
  }
  return `unknown_backgrounds/${file}`;
}

/**
 * @param {string} backgroundImage — either an `/images/` filename (e.g. `snow.jpg`) or `unknown_backgrounds/...`
 * @returns {string} path for CSS `url(...)` (leading slash, no encode)
 */
function resolveBattleBackgroundCssUrl(backgroundImage) {
  if (!backgroundImage || typeof backgroundImage !== 'string') {
    throw new Error('resolveBattleBackgroundCssUrl: backgroundImage is required');
  }
  if (backgroundImage.startsWith('unknown_backgrounds/')) {
    return `/${backgroundImage}`;
  }
  if (backgroundImage.startsWith('images/')) {
    return `/${backgroundImage}`;
  }
  return `/images/${backgroundImage}`;
}

/**
 * Boss mission win: flat darkness meter reduction (absolute percentage points).
 * Must match `EXPLORE_UNKNOWN_BOSS_WIN_DARKNESS_REDUCTION_PERCENT` in `modules/exploreUnknownMissions.js`.
 */
const EXPLORE_UNKNOWN_BOSS_WIN_DARKNESS_REDUCTION_PERCENT = 10;

/**
 * Tactical-loss darkness increase before rematch (absolute percentage points).
 * Applies to every Explore Unknown battle loss (boss and non-boss). L21 reduces this
 * increment on bosses only. Must match `EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT`
 * in `modules/exploreUnknownMissions.js`.
 */
const EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT = 30;

/**
 * Rewrite Experience mission loss retry: darkness increase before retry (absolute percentage points).
 * Must match `REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT` in `modules/exploreUnknownRewriteExperience.js`.
 */
const EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT = 10;

export {
  tryGetExploreUnknownBattleBackgroundPath,
  resolveBattleBackgroundCssUrl,
  getExploreUnknownSurfaceFirstMissionPreviewUrl,
  EXPLORE_UNKNOWN_BOSS_WIN_DARKNESS_REDUCTION_PERCENT,
  EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT,
};
